import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { BASE_URL } from "../Apis/apiConfig";
import { ScrollText, RefreshCw, AlertCircle, FileX, Edit, Trash2, CheckSquare,X, Search,
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import { Download } from "lucide-react";

const BillLists = () => {
  const [billList, setBillList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const safe = (v) =>
    v !== undefined && v !== null && String(v).trim() !== "" ? String(v) : "-";

  const getBillData = async () => {
    setLoading(true);
    setError(null);
    try {
      const societyName = localStorage.getItem("society") ?? "";
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${BASE_URL}/credit/get-bill-by-society-name`,
        null,
        { params: { societyName }, headers },
      );

      const data = Array.isArray(response.data) ? response.data : [];
      setBillList(data);
    } catch (err) {
      console.error("API error:", err);
      setError(err?.response?.data ?? err.message ?? "Failed to fetch");
      setBillList([]);
    } finally {
      setLoading(false);
    }
  };

const handleDownloadExcel = () => {
  try {
    if (!filteredBills || filteredBills.length === 0) {
      Swal.fire("Info", "No data available to export", "info");
      return;
    }

    const excelData = filteredBills.map((row) => {
      const newRow = {};

      Object.keys(row).forEach((key) => {
        if (key !== "id") {
          newRow[key.replace(/_/g, " ").toUpperCase()] = row[key];
        }
      });

      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Bill List"
    );

    const societyName =
      localStorage.getItem("society") || "Society";

    const fileName = `${societyName}_Bill_List_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    // Download Excel directly
    XLSX.writeFile(workbook, fileName);

    Swal.fire(
      "Success",
      "Excel downloaded successfully",
      "success"
    );
  } catch (error) {
    console.error(error);

    Swal.fire(
      "Error",
      "Failed to download excel",
      "error"
    );
  }
};

  useEffect(() => {
    getBillData();
  }, []);

  const filteredBills = useMemo(() => {
    if (!searchTerm.trim()) return billList;

    const searchLower = searchTerm.toLowerCase();
    return billList.filter((bill) =>
      Object.values(bill).some((value) =>
        String(value).toLowerCase().includes(searchLower),
      ),
    );
  }, [billList, searchTerm]);

  const allKeys = useMemo(() => {
    if (filteredBills.length > 0) {
      return Object.keys(filteredBills[0]).filter((key) => key !== "id");
    }
    return [];
  }, [filteredBills]);

//   const allKeys = useMemo(() => {
//   if (filteredBills.length > 0) {
//     return Object.keys(filteredBills[0]).filter(
//       (key) => key !== "id" && key !== "upload_date"
//       &&  key !== "is_previous_year"
//     );
//   }
//   return [];
// }, [filteredBills]);

  const totalItems = filteredBills.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [perPage, totalItems, currentPage, totalPages]);

  const paginatedBills = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredBills.slice(start, start + perPage);
  }, [filteredBills, currentPage, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const openEditModal = (row) => {
    setEditRowData(row);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditRowData(null);
    setIsEditModalOpen(false);
  };

  const handleEditChange = (key, value) => {
    setEditRowData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      const { id, ...updatePayload } = editRowData;

      await axios.put(`${BASE_URL}/api/update-bill/{id}/${id}`, updatePayload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      });

      setBillList((prev) =>
        prev.map((b) => (b.id === id ? { ...b, ...updatePayload } : b)),
      );

      closeEditModal();
      Swal.fire("Success", "Bill updated successfully!", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update bill", "error");
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this bill?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}/credit/delete-bill/${item.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setBillList((prev) => prev.filter((b) => b.id !== item.id));
      Swal.fire("Deleted!", "Bill has been deleted.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete bill", "error");
    }
  };

  const deleteByAdminBillTable = async () => {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");
    if (!societyName) {
      Swal.fire("Error", "Society name is required", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: `This will permanently delete ALL bills for "${societyName}"!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${BASE_URL}/api/delete-by-admin-bill-table`, {
        params: { societyName },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire("Deleted!", "All bills have been deleted!", "success");
      await getBillData();
      setSelectedMembers([]);
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error",
        error.response?.data || "Failed to delete bills",
        "error",
      );
    }
  };

  const handleSelectedDelete = async () => {
    if (selectedMembers.length === 0) {
      Swal.fire("Info", "Please select bills to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedMembers.length} selected bill(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society") || "";

      await axios.delete(`${BASE_URL}/api/delete-admin-by-multiple-id`, {
        params: {
          societyName: societyName,
          multipleIds: selectedMembers,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      Swal.fire("Deleted!", "Selected bills have been deleted!", "success");

      setBillList((prev) =>
        prev.filter((bill) => !selectedMembers.includes(bill.id)),
      );
      setSelectedMembers([]);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete selected bills", "error");
    }
  };

  const getEditableFields = () => {
    if (!editRowData) return [];
    return Object.keys(editRowData).filter((key) => key !== "id");
  };

  const editableFields = getEditableFields();


  const changeToPrevious = async () => {
  try {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");
    await axios.post(
      `${BASE_URL}/api/change-bill-previous-by-admin`,
      null,
      {
        params: {
          societyName,
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    Swal.fire("Success", "Changed to Previous Bills!", "success");

    // 🔄 reload data
    getBillData(true);

  } catch (error) {
    console.error(error);
    Swal.fire("Error", "Failed to update", "error");
  }
};

useEffect(() => {
  updatePreviousMonth();
}, []);

const updatePreviousMonth = async () => {
  try {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");

    const response = await axios.post(
      `${BASE_URL}/api/is-previous-month`,
      null,
      {
        params: { societyName }, 
        headers: {
          Authorization: `Bearer ${token}`, 
        },
      }
    );
  } catch (error) {
    console.error("Error updating previous month:", error);
  }
};

  return (
    <div className="app-container">
      <style>
        {`
/* ========== BILL LIST SPECIFIC OPTIMIZATIONS ========== */
.app-container {
  height: 100vh;
  overflow: hidden !important;
}

.main-layout {
  height: calc(100vh - 64px);
  overflow: hidden !important;
}

.content-area {
  height: 100%;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding-bottom: 20px;
}

.financial-container,
.financial-card {
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
}

.financial-body {
  height: auto !important;
  overflow: visible !important;
  padding: 0 !important;
}

/* ========== COMPACT TABLE CONTROLS (MATCHING MEMBER.JS) ========== */
.compact-table-controls {
  padding: 8px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 44px;
}

.controls-left,
.controls-center,
.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-center {
  flex: 1;
  justify-content: center;
}

/* Entries Selector - Compact */
.entries-selector label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  height: 28px;
}

/* Pagination - Compact */
.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.pagination-nav {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  height: 26px;
  transition: all 0.2s;
}

.pagination-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-nav:not(:disabled):hover {
  background: #f1f5f9;
}

.pagination-btn {
  min-width: 26px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover {
  background: #f1f5f9;
}

.pagination-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 11px;
}

.entries-info {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

/* ========== TABLE OPTIMIZATION ========== */
.financial-table-container {
  overflow-x: auto;
  padding: 0;
  margin: 0;
}

.financial-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

/* Compact Headers */
.financial-table th {
  padding: 6px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  white-space: nowrap;
  font-weight: 600;
  font-size: 11px !important;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.2 !important;
  height: 32px !important;
}

/* Compact Data Cells */
.financial-table td {
  padding: 5px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  font-size: 10px !important;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.3 !important;
  height: 36px !important;
}

/* Checkbox Column */
.financial-table th:nth-child(1),
.financial-table td:nth-child(1) {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
  padding: 5px 2px !important;
}

/* Actions Column */
.financial-table th:last-child,
.financial-table td:last-child {
  width: 80px !important;
  min-width: 80px !important;
  padding: 4px 2px !important;
  position: sticky;
  right: 0;
  background: white;
  z-index: 1;
}

.financial-table thead th:last-child {
  background: #f8fafc;
  z-index: 2;
}

/* Dynamic content columns */
.financial-table th:not(:first-child):not(:last-child),
.financial-table td:not(:first-child):not(:last-child) {
  min-width: 100px !important;
  max-width: 200px !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin: 0;
}

.action-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.action-icon-btn svg {
  width: 14px;
  height: 14px;
}

.action-icon-btn:hover {
  background-color: #f1f5f9;
}

/* Checkbox */
.financial-table input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  vertical-align: middle;
  cursor: pointer;
}

/* Row Hover */
.financial-table tbody tr:hover td {
  background-color: #f8fafc;
}

.financial-table tbody tr:hover td:last-child {
  background-color: #f1f5f9;
}

/* Cell Ellipsis */
.cell-ellipsis {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Empty State */
.financial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 300px;
}

.financial-state h3 {
  margin: 16px 0 8px 0;
  color: #334155;
  font-size: 18px;
  font-weight: 600;
}

.financial-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Edit Modal Styles */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.edit-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.edit-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.edit-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #64748b;
  transition: all 0.2s;
}

.edit-modal-close:hover {
  background: #f1f5f9;
  color: #334155;
}

.edit-modal-body {
  padding: 24px;
}

.edit-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  text-transform: capitalize;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  position: sticky;
  bottom: 0;
  background: white;
}

.btn-cancel {
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e0;
}

.btn-save {
  padding: 8px 16px;
  background: #0d6efd;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover {
  background: #0b5ed7;
}

/* Loading Animation */
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .compact-table-controls {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 16px;
  }
  
  .controls-left,
  .controls-center,
  .controls-right {
    width: 100%;
    justify-content: space-between;
  }
  
  .edit-form-grid {
    grid-template-columns: 1fr;
  }
}
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              <div className="flex px-4 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-tr-lg rounded-tl-lg text-white py-2">
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search bills..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={getBillData}
                      disabled={loading}
                      title="Refresh bill list"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={deleteByAdminBillTable}
                      title="Delete all bills"
                    >
                      <Trash2 size={14} />
                    </button>

                    <button
                      className="rounded border-none gap-2 flex items-center text-left  bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={handleSelectedDelete}
                      disabled={selectedMembers.length === 0}
                      title={`Delete ${selectedMembers.length} selected bill(s)`}
                    >
                      <Trash2 size={14} />
                      <span style={{ fontSize: "11px" }}>
                        {selectedMembers.length}
                      </span>
                      <CheckSquare size={14} />
                    </button>
                  </div>
                </div>
                <div className="controls-right">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * perPage + 1}-
                      {Math.min(currentPage * perPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}
                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>
                    {renderPageNumbers()}
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                    
                  </div>
               <button className="pagination-nav"
                 onClick={changeToPrevious}>
                 Change Previous
                </button>
                </div>
                <button
  className="rounded border-none flex items-center gap-2 bg-green-600 text-white hover:bg-green-700 p-2"
  onClick={handleDownloadExcel}
  title="Download Excel"
>
  <Download size={14} />
  <span style={{ fontSize: "11px" }}>
    Excel
  </span>
</button>
              </div>
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Bills</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={getBillData} style={{ marginTop: "20px", padding: "10px 20px", background: "#0d6efd", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", }}
                    > Try Again </button>
                  </div>
                ) : filteredBills.length === 0 ? (
                  <div className="financial-state">
                    <div style={{ background: "#f8fafc", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "500px", margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3 style={{ margin: "20px 0 10px 0", color: "#334155", fontSize: "20px", fontWeight: "600", }} > No Bills Found </h3>
                      <p style={{ color: "#64748b", fontSize: "14px", margin: 0, lineHeight: "1.5", }} >
                        {searchTerm ? "No bills match your search criteria." : "There are no bills available for this society."}
                      </p>
                      {searchTerm && (
                        <button onClick={() => setSearchTerm("")} style={{ marginTop: "20px", padding: "8px 20px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "6px", color: "#475569",
                            cursor: "pointer", fontSize: "14px", }} > Clear Search </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="financial-table">
                      <thead>
                        <tr>
                          <th>
                            <input type="checkbox" checked={ selectedMembers.length === filteredBills.length && filteredBills.length > 0 }
                              onChange={(e) => { if (e.target.checked) { setSelectedMembers( filteredBills .map((bill) => bill.id)
                                      .filter((id) => id),
                                  );
                                } else {
                                  setSelectedMembers([]);
                                }
                              }}
                            />
                          </th>
                          {allKeys.map((key) => (
                            <th key={key}>{key.replace(/_/g, " ")}</th>
                          ))}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedBills.map((item, idx) => (
                          <tr key={item.id ?? idx}>
                            <td>
                              <input type="checkbox" checked={selectedMembers.includes(item.id)} onChange={(e) => { const id = item.id;
                                  if (!id) return;
                                  if (e.target.checked) {
                                    setSelectedMembers((prev) => [...prev, id]);
                                  } else {
                                    setSelectedMembers((prev) =>
                                      prev.filter((x) => x !== id),
                                    );
                                  }
                                }}
                              />
                            </td>
                            {allKeys.map((key) => (
                              <td key={key}> <span className="cell-ellipsis" title={safe(item[key])} > {safe(item[key])} </span> </td>
                            ))}
                            <td>
                              <div className="action-buttons">
                                <button className="action-icon-btn" onClick={() => openEditModal(item)} title="Edit" > <Edit size={14} color="#0d6efd" /> </button> <button
                                  className="action-icon-btn" onClick={() => handleDelete(item)} title="Delete" > <Trash2 size={14} color="#dc3545" />
                                </button>
                              </div>
                            </td>
                      
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isEditModalOpen && editRowData && (
            <div className="edit-modal-overlay">
              <div className="edit-modal-content">
                <div className="edit-modal-header">
                  <h3 className="edit-modal-title">Edit Bill</h3>
                  <button className="edit-modal-close" onClick={closeEditModal}>
                    <X size={20} />
                  </button>
                </div>

                <div className="edit-modal-body">
                  <div className="edit-form-grid">
                    {editableFields.map((key) => (
                      <div key={key} className="form-group">
                        <label className="form-label">
                          {key.replace(/_/g, " ")}
                        </label>
                        <input type="text"  value={editRowData[key] || ""}
                          onChange={(e) =>
                            handleEditChange(key, e.target.value)
                          }
                          className="form-input"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="form-actions">
                    <button className="btn-cancel" onClick={closeEditModal}>
                      Cancel
                    </button>
                    <button className="btn-save" onClick={handleSaveEdit}>
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BillLists;
